import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';

import { Form, Formik } from 'formik';
import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import { formatPhoneNumber } from 'common/formatters';
import {
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  UTILITY_PROVIDER_MAPPING_USER_TYPE_PARTNER,
} from 'common/utility/constants';
import { isHomebuyerPartner } from 'common/utility/helpers';

import useDispatch from 'client/hooks/useDispatch';
import useBranch from 'client/hooks/useBranch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  getUtilityConfigurationSourceDisplayName,
  isUtilityConfigurationPersonSource,
  isUtilityConfigurationPmsSource,
  UTILITY_CONFIGURATION_SOURCE_ADMIN,
  utilityConfigurationProvenancePropType,
} from 'client/constants/utilityConfigurationSource';
import { hasRoleAdmin, hasRolePartner } from 'common/userRoles';
import {
  isShowMappingConfidenceEnabled,
  isLowConfidenceProvider,
  lowConfidenceToggleLabel,
  isExclusiveProvider,
  getProviderDisplayView,
} from 'client/helpers/coverageConfidence';
import {
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_PROPERTY_MANAGER_GET_AUTOCOMPLETE,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_GENERAL_NOTE_GET,
} from 'api/utilityActionTypes';

import SrButton, {
  SR_BUTTON_HEIGHT_EXTRA_TALL,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import { SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP } from 'client/components/SrInfoOptionCheckbox/SrInfoOptionCheckbox';
import SrBox from 'client/components/SrBox';
import SrExternalLink from 'client/components/SrExternalLink';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import { SR_TAG_THEME_ORANGE_TEXT, SR_TAG_THEME_GREEN_TEXT } from 'client/components/SrTag/SrTag';
import SrUtilityDisableInternetConfirmationModal from 'client/components/SrUtilitySetup/SrUtilityDisableInternetConfirmationModal';
import {
  GENERAL_NOTE_MAX_LENGTH,
  getFormikInstanceRefProps,
  getLoadedGeneralNoteForRequest,
  isGeneralNoteInteractionDisabled,
  isActiveGeneralNoteRequest,
  shouldClearGeneralNoteForIdentity,
  shouldSeedLoadedGeneralNote,
} from 'client/components/SrUtilityAddressRequiredTypesModal/generalNoteField';
import SrUtilityConfigurationSourceMarker from 'client/components/SrUtilityConfigurationSourceMarker/SrUtilityConfigurationSourceMarker';

import './SrUtilityAddressRequiredTypesForm.less';

const getReviewedCopy = (source) => {
  const displayName = getUtilityConfigurationSourceDisplayName(source);

  if (isUtilityConfigurationPersonSource(source)) {
    return {
      title:
        source === UTILITY_CONFIGURATION_SOURCE_ADMIN
          ? `Reviewed by ${displayName}`
          : 'Reviewed by your team',
      helper: 'These utility settings have already been reviewed.',
    };
  }

  if (isUtilityConfigurationPmsSource(source)) {
    return {
      title: `Reviewed via ${displayName} sync`,
      helper: 'These utility settings were set by your PMS sync.',
    };
  }

  return null;
};

export const SrUtilityConfigurationReviewControl = ({
  isDisabled,
  isSaving,
  onReview,
  provenance,
}) => {
  const reviewedCopy = getReviewedCopy(provenance?.source);
  const isReviewed = !!reviewedCopy;
  const content = (
    <>
      <span
        styleName={classnames('reviewControlIcon', { reviewControlIconChecked: isReviewed })}
        aria-hidden="true"
      >
        {isReviewed && (
          <svg styleName="reviewControlCheck" width="12" height="10" viewBox="0 0 12 10">
            <path
              d="M1 5l3 3 7-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span styleName="reviewControlCopy">
        <span styleName="reviewControlTitle">
          {reviewedCopy?.title || (isSaving ? 'Saving…' : 'Mark reviewed & save')}
        </span>
        <span styleName="reviewControlHelper">
          {reviewedCopy?.helper ||
            (isSaving
              ? 'Recording this review.'
              : 'Saves these utility settings and records the review.')}
        </span>
      </span>
    </>
  );

  if (isReviewed) {
    return (
      <div
        data-testid="utility-settings-reviewed"
        styleName="reviewControl reviewControlStatus"
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="mark-utility-settings-reviewed"
      styleName="reviewControl reviewControlAction"
      disabled={isDisabled}
      aria-busy={isSaving}
      onClick={onReview}
    >
      {content}
    </button>
  );
};

SrUtilityConfigurationReviewControl.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onReview: PropTypes.func.isRequired,
  provenance: utilityConfigurationProvenancePropType,
};

SrUtilityConfigurationReviewControl.defaultProps = {
  provenance: null,
};

SrUtilityConfigurationReviewControl.displayName = 'SrUtilityConfigurationReviewControl';

const getFormFields = ({
  values,
  utilityTypes,
  requiredTypes = [],
  utilityPartner,
  onAddProviderClick,
  onEditProviderClick,
  onRemoveProviderClick,
  propertyManagerMatches,
  onAutocompletePropertyManager,
  setConfirmationModalArgs,
  isAdminUser = false,
  isPartnerUser = false,
  confidenceFilterEnabled = false,
  showMappingConfidence,
  showLowConfidenceByTypeId = {},
  setShowLowConfidence,
  generalNoteInteractionDisabled = false,
}) => {
  return [
    {
      id: 'utilityTypeIds',
      type: 'info_checkbox_multi',
      properties: {
        choices: utilityTypes.map(({ id: utilityTypeId, label, iconUrl, utilityProviders }) => {
          const requiredType = requiredTypes.find(({ id }) => id === utilityTypeId);
          const utilityProviderAddedByPartner = !!utilityProviders.find(
            ({ userType }) => userType === UTILITY_PROVIDER_MAPPING_USER_TYPE_PARTNER,
          );
          const baseUtilityProviders =
            requiredType?.needsMappingConfirmation && !utilityProviderAddedByPartner
              ? []
              : utilityProviders;

          // FEAT-C39: when SHOW_MAPPING_CONFIDENCE is on, hide confirmed+low
          // internet providers. Admins can reveal them via a toggle; partners
          // (with the partner flag enabled) get a hard hide and no toggle.
          const confidenceActive =
            isShowMappingConfidenceEnabled(showMappingConfidence) &&
            utilityTypeId === UTILITY_TYPE_ID_INTERNET &&
            (isAdminUser || (isPartnerUser && confidenceFilterEnabled));
          const showLowConfidence = !!showLowConfidenceByTypeId[utilityTypeId];
          // FEAT-D40: exclusivity supersedes confidence. When an exclusive provider exists
          // for this utility type, show ONLY it (labeled "Exclusive", no low-confidence
          // label/toggle) for everyone — admin, partner, and renter. Otherwise fall back to
          // the FEAT-C39 low-confidence view (admins reveal via toggle; partners hard-hide).
          const {
            hasExclusive,
            lowConfidenceCount,
            displayedProviders: _utilityProviders,
          } = getProviderDisplayView(baseUtilityProviders, {
            confidenceActive,
            showLowConfidence: isAdminUser && showLowConfidence,
          });

          return {
            label: (
              <SrFlex align="center" columnGap="5px">
                {utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label}
                <SrBox
                  width="20px"
                  height="20px"
                  backgroundImage={`url("${iconUrl}")`}
                  backgroundSize="cover"
                  marginRight="5px"
                />
              </SrFlex>
            ),
            sublabel: utilityTypeId === UTILITY_TYPE_ID_INTERNET ? '(Non-Required Utility)' : '',
            tagContents: values?.utilityTypeIds?.includes(utilityTypeId) ? 'Disable' : 'Enable',
            tagTheme: values?.utilityTypeIds?.includes(utilityTypeId)
              ? SR_TAG_THEME_ORANGE_TEXT
              : SR_TAG_THEME_GREEN_TEXT,
            value: utilityTypeId,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
            footer: (values?.utilityTypeIds || []).includes(utilityTypeId) && (
              <div>
                <SrText display="block" font="greycliff" size="13" weight="700">
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Providers for client:'
                    : 'Providers for tenant:'}
                </SrText>

                {_utilityProviders.length === 0 &&
                  utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                    <SrText
                      display="block"
                      font="greycliff"
                      size="13"
                      weight="500"
                      lineHeight="20px"
                    >
                      SimpleCover
                    </SrText>
                  )}

                {_utilityProviders.length === 0 &&
                  utilityTypeId !== UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                    <SrText
                      display="block"
                      font="greycliff"
                      size="13"
                      fontStyle="italic"
                      weight="500"
                      lineHeight="20px"
                    >
                      {isHomebuyerPartner(utilityPartner)
                        ? 'Providers are added once the address is ready for a new client.'
                        : 'Providers are added once the address is unlisted from your website and ready for a new tenant.'}
                    </SrText>
                  )}

                {_utilityProviders.map((utilityProvider) => {
                  const note =
                    [utilityProvider.providerNote, utilityProvider.listingProviderNote]
                      .filter(Boolean)
                      .join('\n\n') || null;

                  return (
                    <div key={utilityProvider.id}>
                      <SrFlex justify="between">
                        <SrFlex gap="10px">
                          {utilityProvider.title && (
                            <SrText font="greycliff" weight="500" size="13">
                              {utilityProvider.title}
                            </SrText>
                          )}
                          {confidenceActive && !hasExclusive && isLowConfidenceProvider(utilityProvider) && (
                            <SrText font="greycliff" weight="500" size="13" color="orange300">
                              Low confidence
                            </SrText>
                          )}
                          {isExclusiveProvider(utilityProvider) && (
                            <SrText font="greycliff" weight="500" size="13" color="green">
                              Exclusive
                            </SrText>
                          )}
                          {utilityProvider.url && (
                            <SrExternalLink link={utilityProvider.url}>
                              <SrText
                                font="greycliff"
                                weight="500"
                                size="13"
                                cursor="pointer"
                                color="blue300"
                                decoration="underline"
                              >
                                Website
                              </SrText>
                            </SrExternalLink>
                          )}
                          {utilityProvider.phone && (
                            <SrText font="greycliff" weight="500" size="13">
                              {formatPhoneNumber(utilityProvider.phone)}
                            </SrText>
                          )}
                          {note && (
                            <SrBox
                              textWrap="nowrap"
                              textOverflow="ellipsis"
                              maxWidth="120px"
                              overflow="hidden"
                            >
                              <SrText font="greycliff" weight="500" size="13" color="orange300">
                                {note}
                              </SrText>
                            </SrBox>
                          )}
                        </SrFlex>
                        {utilityTypeId !== UTILITY_TYPE_ID_INTERNET ? (
                          <SrText
                            font="greycliff"
                            weight="700"
                            size="13"
                            cursor="pointer"
                            color="blue300"
                            onClick={() =>
                              onEditProviderClick({
                                utilityTypeId,
                                utilityProvider,
                              })
                            }
                          >
                            Edit
                          </SrText>
                        ) : (
                          <SrText
                            font="greycliff"
                            weight="700"
                            size="13"
                            cursor="pointer"
                            color="red"
                            onClick={() =>
                              onRemoveProviderClick({
                                utilityProvider,
                              })
                            }
                          >
                            Remove
                          </SrText>
                        )}
                      </SrFlex>
                    </div>
                  );
                })}
                {isAdminUser && lowConfidenceCount > 0 && (
                  <SrBox paddingTop="7px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      size="13"
                      display="block"
                      cursor="pointer"
                      color="blue300"
                      onClick={() => setShowLowConfidence(utilityTypeId, !showLowConfidence)}
                    >
                      {lowConfidenceToggleLabel(lowConfidenceCount, showLowConfidence)}
                    </SrText>
                  </SrBox>
                )}
                {utilityTypeId !== UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                  <SrBox paddingTop="7px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      size="13"
                      align="center"
                      display="block"
                      cursor="pointer"
                      color="blue300"
                      onClick={() => onAddProviderClick({ utilityTypeId })}
                    >
                      + Add Provider
                    </SrText>
                  </SrBox>
                )}
              </div>
            ),
          };
        }),
        handleDuringChange: (selectedValue, initialValue, setValue) => {
          if (
            selectedValue === UTILITY_TYPE_ID_INTERNET &&
            initialValue.length > 0 &&
            initialValue.includes(UTILITY_TYPE_ID_INTERNET)
          ) {
            setConfirmationModalArgs({
              handleConfirmation: setValue,
              displayConfirmationModal: true,
            });
          } else {
            setValue();
          }
        },
      },
    },
    {
      id: 'propertyManagerEmail',
      type: 'autocomplete',
      properties: {
        placeholder: 'type email address here...',
        noMargin: true,
        choices: (propertyManagerMatches || []).map(({ email }) => email),
        onSuggestionsFetchRequested: onAutocompletePropertyManager,
        filterFunc: () => {
          return true;
        },
      },
      validations: ['email'],
    },
    {
      // FEAT-B93 — address-level general note shown to renters at the top of their
      // utilities page. The textarea hard-caps at GENERAL_NOTE_MAX_LENGTH; the API
      // re-enforces it server-side.
      id: 'generalNoteToRenter',
      type: 'long_text',
      properties: {
        placeholder: 'e.g. Water is billed via RUBS through the HOA.',
        maxLength: GENERAL_NOTE_MAX_LENGTH,
        showCharacterCount: true,
        disabled: generalNoteInteractionDisabled,
        style: {
          height: '80px',
        },
      },
    },
  ];
};

const SrUtilityAddressRequiredTypesForm = ({
  address,
  utilityTypes,
  requiredTypes,
  utilityPartner,
  initialUtilityTypeIds,
  isSubmitting,
  onAddDocsClick,
  onAddProviderClick,
  onEditProviderClick,
  onRemoveProviderClick,
  onDeleteAddressClick,
  onSubmit,
  onSubmittingChange,
  onClose,
}) => {
  const addressId = address.id;
  const utilityPartnerId = utilityPartner?.id;
  const generalNoteLoadKey = `${addressId}:${utilityPartnerId}`;

  const [propertyManagerMatches, setPropertyManagerMatches] = useState([]);
  const [confirmationModalArgs, setConfirmationModalArgs] = useState({
    displayConfirmationModal: false,
  });
  const [showPropertyManagerEmailFormField, setShowPropertyManagerEmailFormField] = useState(
    !address.propertyManagerEmail,
  );
  const [loadedGeneralNote, setLoadedGeneralNote] = useState(null);
  const [isFormikReady, setIsFormikReady] = useState(false);
  const [seededGeneralNoteKey, setSeededGeneralNoteKey] = useState(null);

  const lastAutocompleteSearchRef = useRef(null);
  const submitIntentRef = useRef(null);
  const reviewSubmissionActiveRef = useRef(false);

  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  const activeGeneralNoteLoadKeyRef = useRef(generalNoteLoadKey);
  const generalNoteRequestIdRef = useRef(0);
  const formikGeneralNoteKeyRef = useRef(generalNoteLoadKey);
  dispatchRef.current = dispatch;
  activeGeneralNoteLoadKeyRef.current = generalNoteLoadKey;

  // FEAT-B93 — load the existing general note and seed only that field once it arrives.
  // We deliberately do NOT use Formik `enableReinitialize`: that would reset the whole
  // form to its initial values when this async load resolves, silently discarding any
  // edits the user made to the other fields (utility types, notify email) in the meantime.
  const formikRef = useRef(null);
  const setFormikRef = useCallback((formik) => {
    formikRef.current = formik;
    setIsFormikReady(!!formik);
  }, []);

  useLayoutEffect(() => {
    if (
      shouldClearGeneralNoteForIdentity({
        formikKey: formikGeneralNoteKeyRef.current,
        activeKey: generalNoteLoadKey,
        hasFormik: isFormikReady,
      })
    ) {
      formikRef.current.setFieldValue('generalNoteToRenter', '', false);
      formikGeneralNoteKeyRef.current = generalNoteLoadKey;
      setSeededGeneralNoteKey(null);
    }
  }, [generalNoteLoadKey, isFormikReady]);

  useEffect(() => {
    setLoadedGeneralNote(null);
    if (!addressId || !utilityPartnerId) {
      return;
    }
    const requestId = generalNoteRequestIdRef.current + 1;
    generalNoteRequestIdRef.current = requestId;
    const requestKey = `${generalNoteLoadKey}:${requestId}`;
    dispatchRef.current(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_GENERAL_NOTE_GET,
      payload: { addressId, utilityPartnerId },
      onError(error) {
        const activeRequestKey =
          `${activeGeneralNoteLoadKeyRef.current}:${generalNoteRequestIdRef.current}`;
        if (isActiveGeneralNoteRequest(requestKey, activeRequestKey)) {
          // Surface a current load failure while leaving the current field value unchanged.
          global.alert(error.message);
        }
      },
    }).then((result) => {
      const generalNote = getLoadedGeneralNoteForRequest({
        result,
        requestKey,
        activeKey: `${activeGeneralNoteLoadKeyRef.current}:${generalNoteRequestIdRef.current}`,
      });
      if (generalNote !== null) {
        setLoadedGeneralNote({ key: generalNoteLoadKey, value: generalNote });
      }
    });
  }, [addressId, utilityPartnerId, generalNoteLoadKey]);

  useEffect(() => {
    if (
      shouldSeedLoadedGeneralNote({
        loadedGeneralNote,
        activeKey: generalNoteLoadKey,
        hasFormik: !!formikRef.current,
      })
    ) {
      formikRef.current.setFieldValue('generalNoteToRenter', loadedGeneralNote.value);
      formikGeneralNoteKeyRef.current = generalNoteLoadKey;
      setSeededGeneralNoteKey(generalNoteLoadKey);
    }
  }, [loadedGeneralNote, generalNoteLoadKey, isFormikReady]);

  const { utilityUser, showMappingConfidence } = useBranch({
    utilityUser: ['utilityUser'],
    showMappingConfidence: ['env', 'SHOW_MAPPING_CONFIDENCE'],
  });
  const isAdminUser = hasRoleAdmin(utilityUser.roles);
  const isPartnerUser = hasRolePartner(utilityUser.roles);
  const confidenceFilterEnabled = !!address.internetCoverageConfidenceFilterEnabled;
  const [showLowConfidenceByTypeId, setShowLowConfidenceByTypeId] = useState({});
  const setShowLowConfidence = (typeId, value) =>
    setShowLowConfidenceByTypeId((prev) => ({ ...prev, [typeId]: value }));

  const initialFormFields = getFormFields({
    utilityTypes,
    utilityPartner,
    onAddProviderClick,
    onEditProviderClick,
    onRemoveProviderClick,
    setConfirmationModalArgs,
  });
  const generalNoteInteractionDisabled = isGeneralNoteInteractionDisabled({
    loadedGeneralNote,
    activeKey: generalNoteLoadKey,
    seededKey: seededGeneralNoteKey,
  });
  const canPersistGeneralNote = !generalNoteInteractionDisabled;

  const handleSubmit = async (values, { setSubmitting }) => {
    const markAsReviewed = submitIntentRef.current === true;
    submitIntentRef.current = null;
    reviewSubmissionActiveRef.current = markAsReviewed;
    onSubmittingChange(true);

    try {
      await onSubmit(
        { ...values, markAsReviewed },
        { canPersistGeneralNote },
      );
    } finally {
      reviewSubmissionActiveRef.current = false;
      setSubmitting(false);
      onSubmittingChange(false);
    }
  };

  return (
    <>
      <Formik
        {...getFormikInstanceRefProps(setFormikRef)}
        validate={createFormValidationFn(initialFormFields)}
        onSubmit={handleSubmit}
        initialValues={createInitialValues({
          formFields: initialFormFields,
          defaultValues: {
            utilityTypeIds: initialUtilityTypeIds,
            propertyManagerEmail: address.propertyManagerEmail,
            generalNoteToRenter: '',
          },
        })}
      >
        {(formikBag) => {
          const isReviewSubmitting =
            isSubmitting &&
            (submitIntentRef.current === true || reviewSubmissionActiveRef.current);
          const formFields = getFormFields({
            values: formikBag.values,
            utilityTypes,
            requiredTypes,
            utilityPartner,
            onAddProviderClick,
            onEditProviderClick,
            onRemoveProviderClick,
            propertyManagerMatches,
            onAutocompletePropertyManager: ({ value }) => {
              lastAutocompleteSearchRef.current = value;
              dispatch(requestUtilityApi, {
                action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_PROPERTY_MANAGER_GET_AUTOCOMPLETE,
                queryId: value,
                payload: {
                  input: value,
                  utilityPartnerId: utilityPartnerId,
                  withRequiredTypes: true,
                  limit: 10,
                },
              }).then(({ propertyManagers }) => {
                if (lastAutocompleteSearchRef.current === value) {
                  setPropertyManagerMatches(propertyManagers);
                }
              });
            },
            setConfirmationModalArgs,
            isAdminUser,
            isPartnerUser,
            confidenceFilterEnabled,
            showMappingConfidence,
            showLowConfidenceByTypeId,
            setShowLowConfidence,
            generalNoteInteractionDisabled,
          });
          const getFormFieldsById = createFormFieldsByIdGetter(formFields);
          const submitForm = (markAsReviewed) => {
            if (formikBag.isSubmitting || submitIntentRef.current !== null) {
              return;
            }

            submitIntentRef.current = markAsReviewed;
            const resetSubmitIntentIfValidationFailed = () => {
              if (submitIntentRef.current !== null) {
                submitIntentRef.current = null;
                const firstErrorFieldId = Object.keys(
                  createFormValidationFn(formFields)(formikBag.values),
                )[0];
                if (firstErrorFieldId) {
                  scrollToFormField({ formFieldId: firstErrorFieldId });
                  const firstErrorField =
                    global.document &&
                    global.document.getElementById(`field-${firstErrorFieldId}`);
                  const firstErrorInput =
                    firstErrorField &&
                    firstErrorField.querySelector('input, textarea, select, button, [tabindex]');
                  if (firstErrorInput) {
                    firstErrorInput.focus();
                  }
                }
              }
            };
            formikBag
              .submitForm()
              .then(resetSubmitIntentIfValidationFailed, resetSubmitIntentIfValidationFailed);
          };

          return (
            <Form
              aria-busy={isSubmitting}
              inert={isSubmitting ? 'inert' : undefined}
            >
              <SrText
                display="block"
                font="greycliff"
                fontSize="20px"
                lineHeight="28px"
                weight="800"
                color="blueGreycliff"
                paddingBottom="8px"
              >
                {isHomebuyerPartner(utilityPartner)
                  ? 'Utility options for clients'
                  : 'Utility options for renters'}
              </SrText>
              <SrUtilityConfigurationSourceMarker
                provenance={address.utilityConfigurationProvenance}
                tooltipId={`utility-configuration-source-${address.id}-callout-tooltip`}
                variant="callout"
              >
                <SrUtilityConfigurationReviewControl
                  isDisabled={isSubmitting}
                  isSaving={isReviewSubmitting}
                  onReview={() => submitForm(true)}
                  provenance={address.utilityConfigurationProvenance}
                />
              </SrUtilityConfigurationSourceMarker>
              <SrFormikField
                formField={getFormFieldsById('utilityTypeIds')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
                margin="0"
              />
              <SrBox paddingTop="30px">
                <SrText
                  font="greycliff"
                  weight="800"
                  lineHeight="25px"
                  fontSize="21px"
                  display="block"
                  paddingBottom="11px"
                >
                  Note to renter
                </SrText>
                <SrText
                  font="greycliff"
                  weight="500"
                  lineHeight="25px"
                  fontSize="16px"
                  display="block"
                  paddingBottom="11px"
                >
                  Add general details about this address, such as RUBS billing or HOA-included
                  services. Add utility-specific details to the utility’s note.
                </SrText>
                <SrFormikField
                  formField={getFormFieldsById('generalNoteToRenter')}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </SrBox>
              <SrBox paddingTop="30px">
                <SrText
                  font="greycliff"
                  weight="800"
                  lineHeight="25px"
                  fontSize="21px"
                  display="block"
                  paddingBottom="11px"
                >
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Do utility companies need move-in documents?'
                    : 'Do utility companies need tenant documents?'}
                </SrText>
                <SrText
                  font="greycliff"
                  weight="500"
                  lineHeight="25px"
                  fontSize="16px"
                  display="block"
                >
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Share documents with your clients so they can set up utilities successfully.'
                    : 'Share documents to tenants so they can set up utilities successfully.'}
                </SrText>
                <SrButton
                  margin="14px 0 0 0"
                  theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  onClick={onAddDocsClick}
                  hasBoxShadow
                >
                  {address.requiredDocs?.length === 0
                    ? '+ Add Documents'
                    : `Edit Documents (${pluralize('file', address.requiredDocs?.length, true)})`}
                </SrButton>
              </SrBox>
              <SrBox paddingTop="30px">
                <SrText
                  font="greycliff"
                  weight="800"
                  lineHeight="25px"
                  fontSize="21px"
                  display="block"
                  paddingBottom="11px"
                >
                  Notify this email when all utilities are completed:
                </SrText>
                {showPropertyManagerEmailFormField && (
                  <SrFormikField
                    formField={getFormFieldsById('propertyManagerEmail')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                )}
                {!showPropertyManagerEmailFormField && (
                  <SrBox paddingTop="8px">
                    <SrText font="greycliff" weight="800">
                      {address.propertyManagerEmail}
                    </SrText>{' '}
                    <SrText
                      font="greycliff"
                      weight="800"
                      color="blue300"
                      cursor="pointer"
                      onClick={() => setShowPropertyManagerEmailFormField(true)}
                    >
                      Edit
                    </SrText>
                  </SrBox>
                )}
              </SrBox>
              <SrBox paddingTop="35px">
                <SrFlex justify="between">
                  <SrButton
                    type="button"
                    disabled={isSubmitting}
                    theme={SR_BUTTON_THEME_ADOBE_GREY}
                    height={SR_BUTTON_HEIGHT_EXTRA_TALL}
                    width="237px"
                    onClick={onClose}
                  >
                    Cancel
                  </SrButton>

                  <SrButton
                    type="button"
                    disabled={isSubmitting || generalNoteInteractionDisabled}
                    onClick={() => submitForm(false)}
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    height={SR_BUTTON_HEIGHT_EXTRA_TALL}
                    width="237px"
                  >
                    Save
                  </SrButton>
                </SrFlex>
              </SrBox>
              {onDeleteAddressClick && (
                <SrBox paddingTop="20px" textAlign="center">
                  <SrText
                    font="greycliff"
                    weight="800"
                    lineHeight="25px"
                    fontSize="16px"
                    display="block"
                    color="red"
                    cursor="pointer"
                    onClick={onDeleteAddressClick}
                  >
                    Delete Address
                  </SrText>
                </SrBox>
              )}
            </Form>
          );
        }}
      </Formik>
      {confirmationModalArgs.displayConfirmationModal && (
        <SrUtilityDisableInternetConfirmationModal
          utilityPartner={utilityPartner}
          closeModal={() => setConfirmationModalArgs({ displayConfirmationModal: false })}
          modalArgs={confirmationModalArgs}
        />
      )}
    </>
  );
};

SrUtilityAddressRequiredTypesForm.propTypes = {
  address: PropTypes.object.isRequired,
  utilityTypes: PropTypes.array.isRequired,
  requiredTypes: PropTypes.array,
  utilityPartner: PropTypes.object.isRequired,
  initialUtilityTypeIds: PropTypes.array.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onAddDocsClick: PropTypes.func.isRequired,
  onAddProviderClick: PropTypes.func.isRequired,
  onEditProviderClick: PropTypes.func.isRequired,
  onRemoveProviderClick: PropTypes.func.isRequired,
  onDeleteAddressClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onSubmittingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilityAddressRequiredTypesForm.displayName = 'SrUtilityAddressRequiredTypesForm';

export default SrUtilityAddressRequiredTypesForm;
